import { useCallback, useEffect, useRef, useState } from "react";

import api from "../../services/api";
import toastError from "../../errors/toastError";

// `/products` paginado no servidor, com "carregar mais" ao rolar.
//
// As telas pediam só a primeira página e ignoravam o `hasMore`: o catálogo com
// mais de 20 itens sumia da lista. Aqui a lista cresce página a página, e
// trocar qualquer filtro volta para a primeira.
//
// `params` é o filtro (o mesmo objeto do GET). `ativo` falso não busca nada —
// é o modal fechado.
const useProdutosPaginados = (
	params,
	{ ativo = true, pageSize = 50, espera = 300, rota = "/products" } = {},
) => {
	const [produtos, setProdutos] = useState([]);
	const [total, setTotal] = useState(0);
	const [temMais, setTemMais] = useState(false);
	const [carregando, setCarregando] = useState(false);
	const [pagina, setPagina] = useState(1);
	const chave = JSON.stringify(params);
	// Resposta velha (filtro anterior, ainda em voo) não pode sobrescrever a
	// nova: quem digita rápido via a lista do termo de antes.
	const versao = useRef(0);

	const carregar = useCallback(
		async (numero = 1) => {
			const minha = ++versao.current;
			setCarregando(true);
			try {
				const { data } = await api.get(rota, {
					params: { ...JSON.parse(chave), pageNumber: numero, pageSize },
				});
				if (minha !== versao.current) return;
				const chegou = data.products || [];
				setProdutos((atual) => {
					if (numero === 1) return chegou;
					// Produto criado ou apagado entre uma página e outra desloca o
					// corte; sem tirar o repetido, a mesma linha aparece duas vezes.
					const vistos = new Set(atual.map((p) => p.id));
					return [...atual, ...chegou.filter((p) => !vistos.has(p.id))];
				});
				setTemMais(Boolean(data.hasMore));
				setTotal(data.count || 0);
				setPagina(numero);
			} catch (err) {
				if (minha === versao.current) toastError(err);
			}
			if (minha === versao.current) setCarregando(false);
		},
		[chave, pageSize, rota],
	);

	useEffect(() => {
		if (!ativo) return undefined;
		const t = setTimeout(() => carregar(1), espera);
		return () => clearTimeout(t);
	}, [carregar, ativo, espera]);

	const carregarMais = useCallback(() => {
		if (temMais && !carregando) carregar(pagina + 1);
	}, [temMais, carregando, carregar, pagina]);

	// Para o `onScroll` do contêiner que rola.
	const aoRolar = useCallback(
		(e) => {
			const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
			if (scrollHeight - (scrollTop + 200) < clientHeight) carregarMais();
		},
		[carregarMais],
	);

	return {
		produtos,
		setProdutos,
		total,
		temMais,
		carregando,
		recarregar: () => carregar(1),
		carregarMais,
		aoRolar,
	};
};

export default useProdutosPaginados;
